import { normalize, type Schema } from 'normalizr';
import { type Observable, of, throwError } from 'rxjs';
import { ajax, type AjaxConfig, type AjaxResponse } from 'rxjs/ajax';
import { catchError, map, mergeMap, take } from 'rxjs/operators';

import { handleAuthRedirect } from './authRedirect';

export type RequestError = Readonly<{
  status: number;
  message: string | object;
}>;

const checkStatus = (response: AjaxResponse<unknown>) => {
  if (response.status >= 200 && response.status < 400) {
    return of(response);
  }
  // RxJS 7+ requires factory function for throwError
  return throwError(() => response);
};

const mapStatusToMessage = (status: number) => {
  switch (status) {
    case 404:
      return 'page not found';
    case 401:
    case 403:
      return 'User is unauthorized to make this request';
    default:
      return 'Unknown server error';
  }
};

export const request = <T>(urlOrRequest: string | AjaxConfig, schema?: Schema): Observable<T> => {
  if (typeof urlOrRequest === 'string') {
    return ajax(urlOrRequest).pipe(
      take(1), // Ensure the observable completes after first emission (RxJS 7+ compatibility)
      mergeMap(checkStatus),
      map((res): T => {
        if (res.responseType === 'json' && res.response && schema) {
          return {
            ...normalize(res.response, schema),
          } as T;
        }
        return res.response as T;
      }),
      catchError((res) => {
        handleAuthRedirect(res.status);
        if (res.responseType === 'json' && res.response) {
          return throwError(() => ({
            status: res.status,
            message: res.response,
          }));
        }
        return throwError(() => ({
          status: res.status,
          message: mapStatusToMessage(res.status),
        }));
      }),
    );
  }
  return ajax(urlOrRequest as AjaxConfig).pipe(
    take(1), // Ensure the observable completes after first emission (RxJS 7+ compatibility)
    mergeMap(checkStatus),
    map((res): T => {
      if (res.responseType === 'json' && res.response && schema) {
        return {
          ...normalize(res.response, schema),
        } as T;
      }
      return res.response as T;
    }),
    catchError((res) => {
      handleAuthRedirect(res.status);
      if (res.responseType === 'json' && res.response) {
        return throwError(() => ({
          status: res.status,
          message: res.response,
        }));
      }
      return throwError(() => ({
        status: res.status,
        message: mapStatusToMessage(res.status),
      }));
    }),
  );
};
